import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { TriStateCheckbox } from '@/components/UI/TriStateCheckbox/TriStateCheckbox';
import Fuse from 'fuse.js';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import style from './FuzzySearchSelect.module.scss';
import cn from 'classnames';
import { RiCloseLine, RiSearch2Line } from 'react-icons/ri';

export type SelectionValue = boolean | 'indeterminate';
export type Column<Item> = {
  id: string;
  title: string;
  width?: number | string;
  cell: (data: Item) => string | number | ReactElement | null;
};
type FuzzySearchSelectProps<Item, ID> = {
  items: Map<ID, Item>;
  columns: Array<Column<Item>>;
  disabled?: boolean;
  disabledItems?: Set<ID>;
  searchIndex?: Array<{ name: string | Array<string>; weight?: number }>;
  selected?: Map<ID, SelectionValue>;
  onSelectionChange?: (selected: Map<ID, Exclude<SelectionValue, 'indeterminate'>>) => void;
  testId?: string;
};
const ItemIdSymbol = Symbol('search_select_item_id');
type ItemWithId<Item, ID> = Item & { [ItemIdSymbol]: ID };
type SelectionUpdateAction<ID> = { type: 'add'; values: Array<ID> } | { type: 'remove'; values: Array<ID> };
const itemsFromMap = <Item, ID>(items: Map<ID, Item>): Array<ItemWithId<Item, ID>> =>
  Array.from(items.entries()).map(([id, item]) => ({ ...item, [ItemIdSymbol]: id }));
const getId = <Item, ID>(item: ItemWithId<Item, ID>): ID => item[ItemIdSymbol];
const maybeTriggerSelectionChange = <Item, ID>(
  selection: Map<ID, SelectionValue>,
  disabledItems: Set<ID> | undefined,
  callback: FuzzySearchSelectProps<Item, ID>['onSelectionChange'] | undefined
) => {
  if (typeof callback === 'function') {
    callback(
      Array.from(selection.entries()).reduce<Map<ID, Exclude<SelectionValue, 'indeterminate'>>>(
        (acc, [key, value]) =>
          typeof value === 'boolean' && !(disabledItems?.has(key) ?? false) ? acc.set(key, value) : acc,
        new Map()
      )
    );
  }
};

export const FuzzySearchSelect = <Item, ID>({
  items,
  columns,
  searchIndex,
  selected: incomingSelected,
  onSelectionChange,
  disabled = false,
  disabledItems,
  testId,
}: FuzzySearchSelectProps<Item, ID>): ReactNode => {
  const [search, updateSearch] = useState<string>('');
  const [selected, updateSelected] = useState<Map<ID, SelectionValue>>(new Map());
  const selectionEnabled = useMemo(() => typeof onSelectionChange === 'function', [onSelectionChange]);

  useEffect(() => {
    updateSelected((prevState) => {
      const update = new Map<ID, SelectionValue>();
      items.forEach((value, key) => {
        update.set(key, prevState.get(key) ?? false);
      });
      return update;
    });
  }, [items]);
  useEffect(() => {
    if (_notNil(incomingSelected) && incomingSelected.size > 0) {
      updateSelected((prevState) => new Map([...prevState, ...incomingSelected]));
    }
  }, [incomingSelected]);

  const updateSelection = useCallback(
    (action: SelectionUpdateAction<ID>) => {
      if (disabled) {
        return;
      }
      switch (action.type) {
        case 'add': {
          updateSelected((prevSelected) => {
            const update = new Map(prevSelected);
            action.values.forEach((id) => update.set(id, true));
            maybeTriggerSelectionChange(update, disabledItems, onSelectionChange);
            return update;
          });
          break;
        }
        case 'remove': {
          updateSelected((prevSelected) => {
            const update = new Map(prevSelected);
            action.values.forEach((id) => update.set(id, false));
            maybeTriggerSelectionChange(update, disabledItems, onSelectionChange);
            return update;
          });
          break;
        }
      }
    },
    [disabled, disabledItems]
  );
  const gridTemplateColumns = useMemo(
    () =>
      columns
        .reduce(
          (acc, column) => [
            ...acc,
            typeof column.width === 'number'
              ? `${column.width}fr`
              : typeof column.width === 'string'
                ? column.width
                : 'auto',
          ],
          selectionEnabled ? [style.selection_column_with.replaceAll('"', '')] : []
        )
        .join(' '),
    [selectionEnabled, columns]
  );

  const searchData = useMemo(() => {
    const itemsWithId = itemsFromMap(items);
    if (_isNotEmpty(searchIndex)) {
      return {
        items: itemsWithId,
        fuse: new Fuse<ItemWithId<Item, ID>>(itemsFromMap(items), {
          includeScore: false,
          threshold: 0.15,
          shouldSort: true,
          ignoreLocation: true,
          keys: searchIndex.map((key) => ({ weight: 0.8, ...key })),
        }),
      };
    }
    return { items: itemsWithId };
  }, [items, searchIndex]);
  const filteredItems = useMemo(() => {
    if (_notNil(searchData.fuse) && _isNotBlank(search)) {
      return searchData.fuse.search(search).map((item) => item.item);
    }
    return searchData.items;
  }, [searchData, search]);

  const selectAllState = useMemo(() => {
    if (filteredItems.length === 0) {
      return false;
    }

    let enabledCount = 0;
    let selectedCount = 0;

    for (const item of filteredItems) {
      const itemId = getId(item);
      if (disabledItems?.has(itemId)) {
        continue;
      }

      enabledCount++;
      const selectionValue = selected.get(itemId) ?? false;

      if (selectionValue === 'indeterminate') {
        return 'indeterminate';
      }

      if (selectionValue === true) {
        selectedCount++;
      }
    }

    if (enabledCount === 0) {
      return false;
    }

    if (selectedCount === enabledCount) {
      return true;
    } else if (selectedCount === 0) {
      return false;
    } else {
      return 'indeterminate';
    }
  }, [selected, filteredItems, disabledItems]);

  const selectionCount = useMemo(
    () => Array.from(selected.values()).reduce((acc, current) => (current === true ? acc + 1 : acc), 0),
    [selected]
  );

  const findLastSelectedIndexAbove = useCallback(
    (currentIndex: number): number => {
      for (let i = currentIndex - 1; i >= 0; i--) {
        const id = getId(filteredItems[i]);
        if (selected.get(id) === true && !(disabledItems?.has(id) ?? false)) {
          return i;
        }
      }
      return 0;
    },
    [filteredItems, selected, disabledItems]
  );

  const findContiguousCheckedItemsAbove = useCallback(
    (currentIndex: number): number => {
      for (let i = currentIndex - 1; i >= 0; i--) {
        const id = getId(filteredItems[i]);
        const isDisabled = disabledItems?.has(id) ?? false;
        const isSelected = selected.get(id) === true;

        if (!isSelected || isDisabled) {
          return i + 1;
        }
      }
      return 0;
    },
    [filteredItems, selected, disabledItems]
  );

  return (
    <div
      className={cn('flex flex-column ba', style.Container, { [style.Disabled]: disabled })}
      data-test-component="FuzzySearchSelect"
      data-test-element="container"
      data-testid={testId}
    >
      {_isNotEmpty(searchIndex) ? (
        <div className={style.SearchContainer} data-test-element="search-container">
          <div style={{ position: 'relative' }}>
            <RiSearch2Line className={style.SearchIcon} />
            <input
              type="text"
              placeholder="Search"
              data-test-element="search-input"
              value={search}
              onChange={(event) => updateSearch(event.target.value ?? '')}
              className={style.SearchInput}
              disabled={disabled}
            />
            {_isNotBlank(search) && !disabled ? (
              <RiCloseLine className={style.ClearIcon} onClick={() => updateSearch('')} />
            ) : null}
          </div>
          <div className={style.SelectedCount}>{selectionCount > 0 ? `${selectionCount} selected` : ''}</div>
        </div>
      ) : null}
      <div className={style.ScrollableContainer}>
        <div className={style.GridContainer} style={{ gridTemplateColumns }}>
          {selectionEnabled ? (
            <div className={style.HeaderCell} data-test-element="select-all-container">
              <TriStateCheckbox
                checked={selectAllState}
                onChange={(value) =>
                  updateSelection(
                    value
                      ? { type: 'add', values: filteredItems.map((item) => getId(item)) }
                      : { type: 'remove', values: filteredItems.map((item) => getId(item)) }
                  )
                }
                disabled={disabled}
              />
            </div>
          ) : null}
          {columns.map((column) => (
            <div key={`${column.id}-header`} data-test-element={`${column.id}-header`} className={style.HeaderCell}>
              {column.title}
            </div>
          ))}

          {filteredItems.map((item) => {
            const id = getId(item);
            const isItemDisabled = disabledItems?.has(id) ?? false;
            return (
              <span
                key={`${id}-row`}
                data-test-element="table-row"
                data-test-key={`${id}`}
                className={cn(style.BodyRow, { [style.DisabledRow]: isItemDisabled })}
              >
                {selectionEnabled ? (
                  <div
                    data-test-element="select-cell"
                    className={style.BodyCell}
                    style={{ gridColumnStart: 1, gridColumnEnd: 2 }}
                  >
                    <TriStateCheckbox
                      checked={selected.get(id) ?? false}
                      onChange={(value, _previous, shiftKey) => {
                        if (shiftKey) {
                          const currentIndex = filteredItems.findIndex((item) => getId(item) === id);
                          const anchorIndex = value
                            ? findLastSelectedIndexAbove(currentIndex)
                            : findContiguousCheckedItemsAbove(currentIndex);
                          const rangeIds = filteredItems
                            .slice(anchorIndex, currentIndex + 1)
                            .map((item) => getId(item))
                            .filter((itemId) => !(disabledItems?.has(itemId) ?? false));

                          updateSelection({ type: value ? 'add' : 'remove', values: rangeIds });
                        } else {
                          updateSelection({ type: value ? 'add' : 'remove', values: [id] });
                        }
                      }}
                      disabled={disabled || isItemDisabled}
                    />
                  </div>
                ) : null}
                {columns.map((column, index) => (
                  <div
                    key={`${id}-${column.id}`}
                    data-test-element={`${column.id}-cell`}
                    className={style.BodyCell}
                    style={{
                      gridColumnStart: index + (selectionEnabled ? 2 : 1),
                      gridColumnEnd: index + (selectionEnabled ? 3 : 2),
                    }}
                  >
                    {column.cell(item)}
                  </div>
                ))}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};
